import { Component, Input } from '@angular/core';

import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { PaymentService } from '../../../../../../../../../services/payment/payment.service';
import { PrivateEventBooking } from '../../../../../../../../../models/scheduled-event';
import { OrganizationConfigService } from '../../../../../../../../../services/organization-config/organization-config.service';
import { PreviewRequestedPaymentEmailModalComponent } from '../preview-requested-payment-email-modal/preview-requested-payment-email-modal.component';

export enum RecipientOption {
  bookingCustomer = 'booking_customer',
  manual = 'manual',
  // Create the request without sending any email. Staff copy the payment link
  // from the pending-request entry and share it themselves (PRO-1387).
  noEmail = 'no_email',
}

@Component({
  selector: 'app-private-event-request-payment-modal',
  templateUrl: './private-event-request-payment-modal.component.html',
  styleUrls: ['./private-event-request-payment-modal.component.css']
})
export class PrivateEventRequestPaymentModalComponent {

  @Input() booking: PrivateEventBooking;
  // The amount selected by the user. If null, request the full balance.
  @Input() amount: number | null;
  // Called after the requested-payment is successfully created. Passed back
  // so the parent component can refresh.
  @Input() submitCallback: () => void;

  public readonly RecipientOption = RecipientOption;

  public recipientOption: RecipientOption = RecipientOption.bookingCustomer;
  public manualEmail = '';
  public customNote = '';

  public loading = false;
  public errorMessage = '';

  constructor(
    public activeModal: NgbActiveModal,
    public orgConfig: OrganizationConfigService,
    private paymentService: PaymentService,
    private modalService: NgbModal
  ) {}

  public getRecipientEmail(): string {
    if (this.recipientOption === RecipientOption.noEmail) {
      return '';
    }
    if (this.recipientOption === RecipientOption.manual) {
      return this.manualEmail.trim();
    }
    return this.booking.booked_by_email;
  }

  public openEmailPreview() {
    const modalRef = this.modalService.open(
      PreviewRequestedPaymentEmailModalComponent,
      { size: 'lg' }
    );
    modalRef.componentInstance.invoiceId = this.booking.invoice_id;
    modalRef.componentInstance.amount = this.amount;
    modalRef.componentInstance.customNote = this.customNote;
  }

  public onSubmit() {
    this.errorMessage = '';

    const recipientEmail = this.getRecipientEmail();
    if (!recipientEmail && (this.recipientOption !== RecipientOption.noEmail)) {
      this.errorMessage = 'A recipient email is required.';
      return;
    }
    if (this.recipientOption === RecipientOption.manual) {
      // HTML5 email validation runs in the form, but double-check here.
      const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail);
      if (!looksLikeEmail) {
        this.errorMessage = 'Enter a valid email address.';
        return;
      }
    }

    this.loading = true;

    this.paymentService
      .requestPayment(
        this.booking.invoice_id,
        this.amount === undefined ? null : this.amount,
        recipientEmail,
        this.customNote
      )
      .then(() => {
        if (this.submitCallback) {
          this.submitCallback();
        }
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.error && reason.error.useful_error_message) {
          this.errorMessage = reason.error.useful_error_message;
        } else {
          this.errorMessage = 'Failed to request payment.';
        }
      })
      .finally(() => { this.loading = false; });
  }
}
